/* OSDirectorTraining.jsx — Feb 2026 (Iter79)
 *
 * Hooked up to the new OS-owned endpoints:
 *   - /os/references (user-saved) + /os/director/references/seed (curated 6)
 *   - /os/projects + /os/media (independent of site_meta — Studio Hero
 *     stays private)
 *   - /os/director/footage/analyze (accepts media_id, no URL pasting)
 *   - /os/director/shots/prompt + /os/director/shots/import
 *   - /os/works (auto-ID, versions, move-to-references)
 *   - /os/director/generation/status (honest connected/not)
 *
 * The 5 OS tabs mount a slice of this file via the `only={}` prop.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Film, Copy, Check, Sparkles, Link as LinkIcon, History, AlertTriangle, Plus, Search, Trash2, Upload, Archive, FolderPlus, Play, ChevronRight } from "lucide-react";
import { OS_API, osHeaders, t } from "./osApi";
import OSPracticeToday from "./OSPracticeToday";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ eyebrow, title, right, children, testid }) {
    return (
        <section className="mb-10" data-testid={testid}>
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-bold mb-2">{eyebrow}</p>
                    <h2 className="font-serif text-2xl text-[#F5F0E8]">{title}</h2>
                </div>
                {right}
            </div>
            {children}
        </section>
    );
}

function CopyBtn({ text, testid }) {
    const [ok, setOk] = useState(false);
    if (!text) return null;
    return (
        <button data-testid={testid} onClick={async () => { try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); } catch {} }}
            className="shrink-0 text-[#F5F0E8]/40 hover:text-[#D4A574] transition-colors">
            {ok ? <Check size={13} className="text-[#7BA05B]" /> : <Copy size={13} />}
        </button>
    );
}

// ============================================================
// 1) TODAY'S PRACTICE — one recommended card + "browse all"
// ============================================================
function AbilitiesPanel({ lang }) {
    const [abilities, setAbilities] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/os/director/abilities`, { headers: osHeaders(), withCredentials: true })
            .then(r => setAbilities(r.data.abilities || []))
            .finally(() => setLoading(false));
    }, []);

    // Choose today's recommended card: first base card from brand_direction.
    // Deterministic so Kaily sees the same card until she acts on it.
    const recommended = useMemo(() => {
        for (const a of abilities) {
            if (a.base.length) return { card: a.base[0], ability: a };
        }
        return null;
    }, [abilities]);

    return (
        <Section eyebrow={t(lang, "Today's Practice", "今日练习")} title={t(lang, "20–30 min, one main ability", "20–30 分钟 · 一个主要能力")} testid="director-abilities">
            {loading && <p className="text-[#F5F0E8]/50 text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />{t(lang, "Loading…", "加载中…")}</p>}

            {recommended && (
                <div className="rounded-xl bg-white/5 border border-[#D4A574]/30 p-6 mb-5" data-testid="recommended-card">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4A574] mb-2">{t(lang, recommended.ability.en, recommended.ability.cn)}</p>
                    <p className="font-serif text-xl text-[#F5F0E8] mb-3">{t(lang, recommended.card.objective_en, recommended.card.objective_cn)}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px] text-[#F5F0E8]/80 leading-relaxed mb-4">
                        <div><strong className="text-[#D4A574]">{t(lang, "Steps:", "步骤：")}</strong> <span className="whitespace-pre-wrap">{t(lang, recommended.card.method_en, recommended.card.method_cn)}</span></div>
                        <div>
                            <p><strong className="text-[#D4A574]">{t(lang, "Deliverable:", "交付：")}</strong> {t(lang, recommended.card.deliverable_en, recommended.card.deliverable_cn)}</p>
                            <p className="mt-1"><strong className="text-[#D4A574]">{t(lang, "Done when:", "完成标准：")}</strong> {t(lang, recommended.card.criteria_en, recommended.card.criteria_cn)}</p>
                            <p className="mt-1"><strong className="text-[#D4A574]">{t(lang, "Reflect:", "复盘：")}</strong> {t(lang, recommended.card.reflect_q_en, recommended.card.reflect_q_cn)}</p>
                            <p className="mt-1 text-[#F5F0E8]/50 text-[11px]">{recommended.card.est_min} min</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a href="#works-new" data-testid="start-practice" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold">{t(lang, "Upload my result", "上传结果")}</a>
                        <button onClick={() => setShowAll(true)} data-testid="browse-all" className="rounded-full border border-[#F5F0E8]/25 text-[#F5F0E8]/70 px-5 py-2 text-[11px] uppercase tracking-[0.14em]">{t(lang, "Different task", "换一个任务")}</button>
                    </div>
                </div>
            )}

            <button onClick={() => setShowAll(!showAll)} data-testid="toggle-all" className="text-[11px] uppercase tracking-[0.14em] text-[#D4A574] hover:underline">
                {showAll ? t(lang, "Hide library", "收起全部") : t(lang, "Browse all practices", "浏览全部练习")}
                <ChevronRight size={11} className={`inline ml-1 transition-transform ${showAll ? "rotate-90" : ""}`} />
            </button>

            {showAll && (
                <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {abilities.map((ab) => (
                        <div key={ab.key} className="rounded-xl bg-white/3 border border-white/8 p-5" data-testid={`ability-${ab.key}`}>
                            <p className="font-serif text-lg text-[#F5F0E8] mb-1">{t(lang, ab.en, ab.cn)}</p>
                            <p className="text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/35 mb-4">{ab.base.length} {t(lang, "base", "基础")} · {ab.advanced.length} {t(lang, "advanced", "进阶")}</p>
                            <div className="space-y-2">
                                {[...ab.base, ...ab.advanced].slice(0, 6).map((c, i) => (
                                    <div key={c.slug} className="text-[11.5px] text-[#F5F0E8]/70 leading-relaxed">
                                        <span className={i < ab.base.length ? "text-[#D4A574]" : "text-[#C08FBF]"}>{i < ab.base.length ? "•" : "★"} </span>
                                        {t(lang, c.objective_en, c.objective_cn)}
                                        <span className="text-[#F5F0E8]/35 ml-2 text-[10.5px]">· {c.est_min}m</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
}

// ============================================================
// 2) REFERENCES — Mine + Curated (seed)
// ============================================================
function ReferencesPanel({ lang }) {
    const [tab, setTab] = useState("mine");
    const [mine, setMine] = useState([]);
    const [seed, setSeed] = useState([]);
    const [q, setQ] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [editing, setEditing] = useState(null);

    const refresh = async () => {
        try {
            const [m, s] = await Promise.all([
                axios.get(`${API}/os/references${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: osHeaders(), withCredentials: true }),
                axios.get(`${API}/os/director/references/seed`, { headers: osHeaders(), withCredentials: true }),
            ]);
            setMine(m.data.references || []);
            setSeed(s.data.references || []);
        } catch {}
    };
    useEffect(() => { refresh(); }, [q]);

    return (
        <Section eyebrow={t(lang, "References", "参考库")} title={t(lang, "My reference library", "我的参考库")}
            right={<button onClick={() => { setEditing(null); setShowNew(true); }} data-testid="ref-add" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-bold"><Plus size={12} className="inline mr-1" /> {t(lang, "Add reference", "添加参考")}</button>}
            testid="director-references">

            <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setTab("mine")} data-testid="ref-tab-mine" className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${tab === "mine" ? "bg-[#D4A574] text-[#0D0A08]" : "text-[#F5F0E8]/60 border border-white/15"}`}>{t(lang, "My references", "我的参考")} ({mine.length})</button>
                <button onClick={() => setTab("seed")} data-testid="ref-tab-seed" className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${tab === "seed" ? "bg-[#D4A574] text-[#0D0A08]" : "text-[#F5F0E8]/60 border border-white/15"}`}>{t(lang, "Recommended sources", "推荐来源")} ({seed.length})</button>
                {tab === "mine" && (
                    <div className="ml-auto flex items-center gap-2 bg-black/30 border border-white/15 rounded-full px-3 py-1.5">
                        <Search size={12} className="text-[#F5F0E8]/40" />
                        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t(lang, "Search", "搜索")} data-testid="ref-search" className="bg-transparent text-[12px] text-[#F5F0E8] focus:outline-none w-32" />
                    </div>
                )}
            </div>

            {showNew && (
                <ReferenceForm
                    lang={lang}
                    initial={editing}
                    onCancel={() => setShowNew(false)}
                    onSaved={() => { setShowNew(false); refresh(); }}
                />
            )}

            {tab === "mine" && (
                <div className="space-y-3">
                    {mine.length === 0 && <p className="text-[12px] text-[#F5F0E8]/40 italic">{t(lang, "No saved references yet — tap Add reference to save your first.", "暂无收藏参考 — 点右上角「添加参考」开始收藏。")}</p>}
                    {mine.map(r => (
                        <div key={r._id} className="rounded-xl bg-white/3 border border-white/8 p-4" data-testid={`my-ref-${r._id}`}>
                            <div className="flex items-start gap-3">
                                {r.cover_url && <img src={r.cover_url} alt="" className="w-24 h-24 object-cover rounded-md shrink-0" />}
                                <div className="min-w-0 flex-1">
                                    <p className="font-serif text-base text-[#F5F0E8]">{r.title}</p>
                                    <p className="text-[11px] text-[#F5F0E8]/50 mt-0.5">{r.author || "—"} {r.tags?.length ? `· ${r.tags.join(" / ")}` : ""}</p>
                                    {r.source_url && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#D4A574] hover:underline inline-flex items-center gap-1 mt-1"><LinkIcon size={10} /> {r.source_url.slice(0, 60)}</a>}
                                    {r.note && <p className="text-[12px] text-[#F5F0E8]/70 mt-2 line-clamp-2">{r.note}</p>}
                                    <p className="text-[10px] text-[#F5F0E8]/35 mt-2">{t(lang, "Saved link only — attach a screenshot or clip for AI analysis.", "仅保存链接 — 若要分析请附截图或片段。")}</p>
                                </div>
                                <div className="flex flex-col gap-1 items-end shrink-0">
                                    <button onClick={() => { setEditing(r); setShowNew(true); }} className="text-[10px] uppercase tracking-[0.14em] text-[#F5F0E8]/60 hover:text-[#D4A574]">{t(lang, "Edit", "编辑")}</button>
                                    <button onClick={async () => { if (window.confirm(t(lang, "Delete this reference?", "删除此参考？"))) { await axios.delete(`${API}/os/references/${r._id}`, { headers: osHeaders(), withCredentials: true }); refresh(); } }} className="text-[10px] uppercase tracking-[0.14em] text-[#C0604A] hover:text-[#C0604A]/80"><Trash2 size={10} className="inline" /> {t(lang, "Del", "删除")}</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "seed" && (
                <>
                    <p className="text-[12px] text-[#F5F0E8]/45 mb-4">{t(lang, "Study references — no partnership or endorsement implied.", "学习参考 — 不代表合作或背书。")}</p>
                    <div className="space-y-3">
                        {seed.map(r => (
                            <div key={r.slug} className="rounded-xl bg-white/3 border border-white/8 p-4" data-testid={`seed-ref-${r.slug}`}>
                                <p className="font-serif text-base text-[#F5F0E8]">{r.title}</p>
                                <p className="text-[11px] text-[#F5F0E8]/50 mt-0.5">{t(lang, r.lens_en, r.lens_cn)}</p>
                                <a href={r.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#D4A574] hover:underline inline-flex items-center gap-1 mt-1"><LinkIcon size={10} /> {r.url}</a>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Section>
    );
}

function ReferenceForm({ lang, initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial || { title: "", author: "", source_url: "", cover_url: "", note: "", want_to_try: "", tags: [], archived: false });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const u = (k) => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const save = async () => {
        setSaving(true); setErr(null);
        try {
            const body = { ...form, tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map(s => s.trim()).filter(Boolean) };
            if (initial?._id) await axios.patch(`${API}/os/references/${initial._id}`, body, { headers: osHeaders(), withCredentials: true });
            else await axios.post(`${API}/os/references`, body, { headers: osHeaders(), withCredentials: true });
            onSaved();
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
        finally { setSaving(false); }
    };

    const field = { className: "w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-[13px] text-[#F5F0E8] focus:outline-none focus:border-[#D4A574]" };

    return (
        <div className="rounded-xl bg-white/5 border border-[#D4A574]/30 p-5 mb-5" data-testid="ref-form">
            <input {...field} value={form.title} onChange={u("title")} placeholder={t(lang, "Title *", "标题 *")} data-testid="ref-form-title" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <input {...field} value={form.author || ""} onChange={u("author")} placeholder={t(lang, "Author", "作者")} />
                <input {...field} value={form.source_url || ""} onChange={u("source_url")} placeholder={t(lang, "Source URL (IG / YouTube / etc.)", "来源链接（IG / YouTube 等）")} />
                <input {...field} value={form.cover_url || ""} onChange={u("cover_url")} placeholder={t(lang, "Cover image URL (optional)", "封面图 URL（选填）")} />
                <input {...field} value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags || ""} onChange={e => setForm(p => ({ ...p, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))} placeholder={t(lang, "Tags (comma-separated)", "分类标签（逗号分隔）")} />
            </div>
            <textarea {...field} value={form.note || ""} onChange={u("note")} rows={3} placeholder={t(lang, "Learning note — what you noticed, at what moment", "学习笔记 — 你注意到什么、什么时刻")} className={`${field.className} mt-3`} />
            <textarea {...field} value={form.want_to_try || ""} onChange={u("want_to_try")} rows={2} placeholder={t(lang, "What you want to try borrowing", "想尝试借用的方法")} className={`${field.className} mt-3`} />
            {err && <p className="text-[#C0604A] text-[12px] mt-2">{err}</p>}
            <div className="flex gap-2 mt-3">
                <button onClick={save} disabled={saving || !form.title} data-testid="ref-form-save" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold disabled:opacity-40">{saving ? "…" : t(lang, "Save", "保存")}</button>
                <button onClick={onCancel} className="text-[11px] text-[#F5F0E8]/50 hover:text-[#F5F0E8] px-3">{t(lang, "Cancel", "取消")}</button>
            </div>
        </div>
    );
}

// ============================================================
// 3) CREATIVE LAB — projects + direct OS upload + analysis + shots
// ============================================================
function CreativeLabPanel({ lang }) {
    const [projects, setProjects] = useState([]);
    const [selected, setSelected] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [genStatus, setGenStatus] = useState(null);

    const refresh = async () => {
        try {
            const r = await axios.get(`${API}/os/projects`, { headers: osHeaders(), withCredentials: true });
            setProjects(r.data.projects || []);
        } catch {}
    };
    useEffect(() => {
        refresh();
        axios.get(`${API}/os/director/generation/status`, { headers: osHeaders(), withCredentials: true })
            .then(r => setGenStatus(r.data)).catch(() => {});
    }, []);

    if (selected) return <ProjectDetail projectId={selected} lang={lang} genStatus={genStatus} onBack={() => { setSelected(null); refresh(); }} />;

    return (
        <Section eyebrow={t(lang, "Creative Lab", "创意实验室")} title={t(lang, "Film creation & AI shot gaps", "影片创作与 AI 补镜头")}
            right={<button onClick={() => setShowNew(true)} data-testid="proj-add" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-bold"><FolderPlus size={12} className="inline mr-1" /> {t(lang, "New project", "新建项目")}</button>}
            testid="director-footage">

            {showNew && <NewProjectForm lang={lang} onCancel={() => setShowNew(false)} onSaved={(id) => { setShowNew(false); setSelected(id); }} />}

            <div className="space-y-2">
                {projects.length === 0 && !showNew && (
                    <div className="rounded-xl bg-white/3 border border-white/8 p-6 text-center">
                        <p className="text-[13px] text-[#F5F0E8]/60 mb-4">{t(lang, "No projects yet.", "还没有项目。")}</p>
                        <div className="flex flex-wrap justify-center gap-2">
                            <button onClick={() => setShowNew("script")} className="text-[11px] uppercase tracking-[0.14em] text-[#D4A574] border border-[#D4A574]/40 rounded-full px-4 py-2">{t(lang, "I have a script", "我已有脚本")}</button>
                            <button onClick={() => setShowNew("footage")} className="text-[11px] uppercase tracking-[0.14em] text-[#D4A574] border border-[#D4A574]/40 rounded-full px-4 py-2">{t(lang, "I have footage", "我已有素材")}</button>
                            <button onClick={() => setShowNew("both")} className="text-[11px] uppercase tracking-[0.14em] text-[#D4A574] border border-[#D4A574]/40 rounded-full px-4 py-2">{t(lang, "I have both", "脚本 + 素材")}</button>
                        </div>
                    </div>
                )}
                {projects.map(p => (
                    <button key={p._id} onClick={() => setSelected(p._id)} data-testid={`project-${p._id}`} className="w-full text-left rounded-xl bg-white/3 border border-white/8 p-4 hover:border-[#D4A574]/40 transition-colors">
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="font-serif text-base text-[#F5F0E8]">{p.name}</p>
                                <p className="text-[11px] text-[#F5F0E8]/50 mt-0.5">{p.target_duration_sec ? `${p.target_duration_sec}s` : ""} {p.target_aspect || ""} · {p.status} · {p.entry_type || "both"}</p>
                            </div>
                            <ChevronRight size={16} className="text-[#F5F0E8]/40 shrink-0" />
                        </div>
                    </button>
                ))}
            </div>
        </Section>
    );
}

function NewProjectForm({ lang, onCancel, onSaved, defaultEntry }) {
    const [name, setName] = useState("");
    const [intent, setIntent] = useState("");
    const [script, setScript] = useState("");
    const [targetSec, setTargetSec] = useState("");
    const [aspect, setAspect] = useState("9:16");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const save = async () => {
        setSaving(true); setErr(null);
        try {
            const r = await axios.post(`${API}/os/projects`, {
                name, story_intent: intent || null, script: script || null,
                target_duration_sec: targetSec ? Number(targetSec) : null,
                target_aspect: aspect, entry_type: typeof defaultEntry === "string" ? defaultEntry : "both",
            }, { headers: osHeaders(), withCredentials: true });
            onSaved(r.data.id);
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
        finally { setSaving(false); }
    };
    const field = "w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-[13px] text-[#F5F0E8] focus:outline-none focus:border-[#D4A574]";

    return (
        <div className="rounded-xl bg-white/5 border border-[#D4A574]/30 p-5 mb-5" data-testid="proj-new">
            <input className={field} value={name} onChange={e => setName(e.target.value)} placeholder={t(lang, "Project name *", "项目名称 *")} data-testid="proj-name" />
            <textarea className={`${field} mt-3`} value={intent} onChange={e => setIntent(e.target.value)} rows={2} placeholder={t(lang, "Story intent (optional, can add later)", "故事意图（选填，可稍后补）")} />
            <textarea className={`${field} mt-3`} value={script} onChange={e => setScript(e.target.value)} rows={4} placeholder={t(lang, "Script (optional)", "脚本（选填）")} data-testid="proj-script" />
            <div className="grid grid-cols-2 gap-3 mt-3">
                <input className={field} value={targetSec} onChange={e => setTargetSec(e.target.value)} placeholder={t(lang, "Target sec (optional)", "目标秒数（选填）")} />
                <select className={field} value={aspect} onChange={e => setAspect(e.target.value)}>
                    <option value="9:16">9:16</option>
                    <option value="16:9">16:9</option>
                    <option value="1:1">1:1</option>
                </select>
            </div>
            {err && <p className="text-[#C0604A] text-[12px] mt-2">{err}</p>}
            <div className="flex gap-2 mt-3">
                <button onClick={save} disabled={saving || !name} data-testid="proj-save" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold disabled:opacity-40">{saving ? "…" : t(lang, "Create & open", "创建并打开")}</button>
                <button onClick={onCancel} className="text-[11px] text-[#F5F0E8]/50 hover:text-[#F5F0E8] px-3">{t(lang, "Cancel", "取消")}</button>
            </div>
        </div>
    );
}

function ProjectDetail({ projectId, lang, genStatus, onBack }) {
    const [doc, setDoc] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [err, setErr] = useState(null);

    const refresh = async () => {
        const r = await axios.get(`${API}/os/projects/${projectId}`, { headers: osHeaders(), withCredentials: true });
        setDoc(r.data);
    };
    useEffect(() => { refresh(); }, [projectId]);

    const analyzeMedia = async (media) => {
        if (!doc?.project?.story_intent) {
            alert(t(lang, "Please add story intent first (edit project).", "请先填写故事意图（在项目信息里编辑）。"));
            return;
        }
        setAnalyzing(true); setErr(null); setAnalysis(null);
        try {
            const r = await axios.post(`${API}/os/director/footage/analyze`, {
                media_id: media._id,
                story_intent: doc.project.story_intent,
                script: doc.project.script,
                target_duration_sec: doc.project.target_duration_sec,
                target_aspect: doc.project.target_aspect,
            }, { headers: osHeaders(), withCredentials: true, timeout: 180000 });
            if (r.data.ok) setAnalysis(r.data.analysis);
            else setErr(r.data.error || "Analysis failed");
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
        finally { setAnalyzing(false); }
    };

    if (!doc) return <p className="text-[#F5F0E8]/40 text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /></p>;

    return (
        <div data-testid="project-detail">
            <button onClick={onBack} className="text-[11px] uppercase tracking-[0.14em] text-[#D4A574] mb-4">← {t(lang, "All projects", "返回项目列表")}</button>
            <h2 className="font-serif text-2xl text-[#F5F0E8] mb-2">{doc.project.name}</h2>
            <p className="text-[12px] text-[#F5F0E8]/50 mb-6">
                {doc.project.story_intent || t(lang, "No story intent yet", "尚未填写故事意图")} · {doc.project.target_duration_sec || "?"}s · {doc.project.target_aspect || "?"}
            </p>

            {/* Step 1 — Upload footage */}
            <div className="rounded-xl bg-white/3 border border-white/8 p-5 mb-5">
                <p className="font-serif text-lg text-[#F5F0E8] mb-3">{t(lang, "1. Upload footage", "一、上传素材")}</p>
                <ProjectUploader projectId={projectId} onDone={refresh} lang={lang} />
                {doc.media.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                        {doc.media.map(m => (
                            <div key={m._id} className="rounded-lg bg-black/40 border border-white/10 overflow-hidden" data-testid={`media-${m._id}`}>
                                {m.url ? (
                                    <video src={m.url} className="w-full aspect-video object-cover bg-black" muted playsInline controls preload="metadata" />
                                ) : (
                                    <div className="aspect-video flex items-center justify-center text-[11px] text-[#F5F0E8]/40">{m.status}</div>
                                )}
                                <div className="p-2 text-[11px] text-[#F5F0E8]/70 flex items-center justify-between">
                                    <span className="truncate">{m.filename || m._id.slice(0, 8)}</span>
                                    <button onClick={() => analyzeMedia(m)} disabled={analyzing} data-testid={`analyze-${m._id}`} className="text-[10px] uppercase tracking-[0.14em] text-[#D4A574] hover:underline disabled:opacity-40">
                                        {analyzing ? "…" : t(lang, "Analyse", "分析")}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Step 2 — Analysis + gaps */}
            {analyzing && <p className="text-[#F5F0E8]/60 text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />{t(lang, "Gemini is reading your footage…", "Gemini 正在读取素材…")}</p>}
            {err && <p className="text-[#C0604A] text-[12px] mt-2">{err}</p>}
            {analysis && (
                <div className="rounded-xl bg-white/3 border border-white/8 p-5 mb-5" data-testid="analysis-result">
                    <p className="font-serif text-lg text-[#F5F0E8] mb-3">{t(lang, "2. Observations & gaps", "二、素材清单与缺口")}</p>
                    {analysis.existing_footage_sufficient && <div className="rounded-lg bg-[#7BA05B]/15 border border-[#7BA05B]/40 p-3 text-[12px] text-[#7BA05B] mb-4">{t(lang, "Existing footage is sufficient — reorder / trim; no new shots needed.", "现有素材已足够 — 重排或精剪即可，无需补镜头。")}</div>}
                    <div className="space-y-2 mb-5">
                        {(analysis.observations || []).map((o, i) => (
                            <div key={i} className="rounded-lg bg-black/30 border border-white/8 p-3 text-[12px]">
                                <p className="text-[#D4A574] font-mono text-[11px]">{o.t_start} – {o.t_end} · {o.shot_size} · {o.camera_movement} · {o.confidence}</p>
                                <p className="text-[#F5F0E8]/85 mt-1">{o.subject} — {o.action}</p>
                                <p className="text-[#F5F0E8]/60 mt-0.5">{t(lang, "Lighting:", "光：")} {o.lighting} · {t(lang, "Audio:", "声：")} {o.audio}</p>
                                <p className="text-[#F5F0E8]/50 italic mt-0.5">→ {o.usable_for}</p>
                            </div>
                        ))}
                    </div>
                    {(analysis.gaps || []).length > 0 && (
                        <>
                            <p className="font-serif text-base text-[#F5F0E8] mb-2">{t(lang, "Shot gaps", "缺口清单")}</p>
                            <div className="space-y-2">
                                {analysis.gaps.map((g, i) => <GapRow key={i} gap={g} lang={lang} index={i} genStatus={genStatus} />)}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function ProjectUploader({ projectId, onDone, lang }) {
    const [progress, setProgress] = useState({});
    const inputRef = useRef(null);

    const upload = async (file) => {
        const key = `${file.name}-${file.size}`;
        setProgress(p => ({ ...p, [key]: { pct: 0, status: "signing", name: file.name, size: file.size } }));

        // Server proxy path — no CORS needed, 100 MB cap.
        const proxyUpload = async () => {
            const form = new FormData();
            form.append("file", file);
            if (projectId) form.append("project_id", projectId);
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("POST", `${API}/os/media/upload-direct`);
                const h = osHeaders() || {};
                Object.entries(h).forEach(([k, v]) => xhr.setRequestHeader(k, v));
                xhr.withCredentials = true;
                xhr.upload.onprogress = e => e.lengthComputable && setProgress(p => ({ ...p, [key]: { ...p[key], pct: Math.round((e.loaded / e.total) * 100) } }));
                xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Server ${xhr.status}: ${xhr.responseText?.slice(0, 120)}`));
                xhr.onerror = () => reject(new Error("Network error (server proxy)"));
                xhr.send(form);
            });
        };

        try {
            if (file.size <= 100 * 1024 * 1024) {
                setProgress(p => ({ ...p, [key]: { ...p[key], status: "uploading" } }));
                await proxyUpload();
                setProgress(p => { const n = { ...p }; delete n[key]; return n; });
                onDone?.();
                return;
            }
            // Large file → presigned direct
            const { data: presign } = await axios.post(`${API}/os/media/presign-upload`, {
                content_type: file.type || "video/mp4", size: file.size, filename: file.name, project_id: projectId,
            }, { headers: osHeaders(), withCredentials: true });
            setProgress(p => ({ ...p, [key]: { ...p[key], status: "uploading" } }));
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", presign.upload_url);
                xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
                xhr.upload.onprogress = e => e.lengthComputable && setProgress(p => ({ ...p, [key]: { ...p[key], pct: Math.round((e.loaded / e.total) * 100) } }));
                xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`R2 ${xhr.status}`));
                xhr.onerror = () => reject(new Error("CORS_OR_NETWORK"));
                xhr.send(file);
            });
            await axios.post(`${API}/os/media/confirm-upload`, { media_id: presign.media_id, key: presign.key }, { headers: osHeaders(), withCredentials: true });
            setProgress(p => { const n = { ...p }; delete n[key]; return n; });
            onDone?.();
        } catch (err) {
            const msg = err?.message === "CORS_OR_NETWORK"
                ? "R2 bucket CORS policy missing for this site. Apply the CORS JSON in Cloudflare dashboard, or upload a file ≤ 100 MB (server proxy works without CORS)."
                : (err?.message || "Upload failed");
            setProgress(p => ({ ...p, [key]: { ...p[key], status: "error", error: msg } }));
        }
    };

    return (
        <div>
            <input ref={inputRef} type="file" accept="video/*" multiple className="hidden" onChange={e => { Array.from(e.target.files || []).forEach(upload); e.target.value = ""; }} data-testid="os-media-file" />
            <button onClick={() => inputRef.current?.click()} data-testid="os-media-upload" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold">
                <Upload size={13} className="inline mr-1" /> {t(lang, "Choose videos (up to 500 MB each)", "选择视频（单文件最大 500 MB）")}
            </button>
            <p className="text-[10.5px] text-[#F5F0E8]/35 mt-2">{t(lang, "OS-private — never pushed to the public site.", "OS 私密 — 不会同步到公开网站。")}</p>
            {Object.entries(progress).map(([k, v]) => (
                <div key={k} className="mt-3 p-3 rounded bg-black/30 border border-white/10 text-[11px]">
                    <p className="text-[#F5F0E8]/85">{v.name} · {(v.size / (1024 * 1024)).toFixed(1)} MB · {v.status}</p>
                    {v.status === "uploading" && <div className="h-1.5 bg-white/10 rounded mt-2 overflow-hidden"><div className="h-full bg-[#D4A574]" style={{ width: `${v.pct}%` }} /></div>}
                    {v.status === "error" && <p className="text-[#C0604A] mt-1">{v.error}</p>}
                </div>
            ))}
        </div>
    );
}

function GapRow({ gap, lang, index, genStatus }) {
    const [prompt, setPrompt] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [importUrl, setImportUrl] = useState("");
    const [importTool, setImportTool] = useState("Runway Gen-3");

    const genPrompt = async () => {
        setBusy(true); setErr(null); setPrompt(null);
        try {
            const { data } = await axios.post(`${API}/os/director/shots/prompt`, {
                gap_label: gap.label,
                gap_description: `${gap.why_needed} · ${gap.composition} · ${gap.action} · ${gap.lighting}`,
                target_aspect: "9:16",
                style_notes: gap.audio,
            }, { headers: osHeaders(), withCredentials: true });
            if (data.ok) setPrompt(data.prompt);
            else setErr(data.error || "Prompt failed");
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };

    const doImport = async () => {
        try {
            await axios.post(`${API}/os/director/shots/import`, { external_url: importUrl, source_tool: importTool, label: gap.label }, { headers: osHeaders(), withCredentials: true });
            setImportUrl(""); alert(t(lang, "Imported.", "已导入。"));
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    };

    const generationConnected = genStatus?.connected;

    return (
        <div className="rounded-lg bg-black/30 border border-white/8 p-3 text-[12px]" data-testid={`gap-${index}`}>
            <p className="text-[#C08FBF] font-semibold mb-1">{gap.label} <span className="text-[10px] uppercase tracking-[0.14em] text-[#F5F0E8]/40 ml-2">{gap.resolve_via}</span></p>
            <p className="text-[#F5F0E8]/70">{gap.why_needed}</p>
            <p className="text-[#F5F0E8]/60 mt-1 text-[11.5px]">{t(lang, "Pos:", "位置：")} {gap.suggest_pos} · {gap.suggest_dur} · {gap.composition} · {gap.lighting}</p>
            <p className="text-[#F5F0E8]/50 italic mt-0.5 text-[11.5px]">{t(lang, "Connect:", "连接：")} {gap.connect_to}</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button onClick={genPrompt} disabled={busy} data-testid={`gap-prompt-${index}`} className="text-[11px] uppercase tracking-[0.14em] text-[#D4A574] border border-[#D4A574]/40 rounded-full px-3 py-1 hover:bg-[#D4A574]/10 disabled:opacity-40">
                    {busy ? <Loader2 size={11} className="animate-spin inline mr-1" /> : <Sparkles size={11} className="inline mr-1" />}
                    {t(lang, "Prepare generation plan", "准备生成方案")}
                </button>
            </div>
            {err && <p className="text-[#C0604A] text-[11px] mt-2">{err}</p>}
            {prompt && (
                <div className="mt-3 pt-3 border-t border-white/8 text-[11.5px] space-y-2">
                    {!generationConnected && (
                        <div className="rounded bg-[#D4A574]/10 border border-[#D4A574]/30 text-[#D4A574] p-2 text-[11px] flex items-start gap-2" data-testid={`gap-${index}-not-connected`}>
                            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                            <span>{t(lang, "No in-OS generation service is connected (fal.ai / Luma / Runway key missing). Copy the prompt into your tool of choice and import the result.", "尚未接入站内视频生成服务（fal.ai / Luma / Runway 密钥缺失）。请复制提示词到你选择的工具，再把结果 URL 导入。")}</span>
                        </div>
                    )}
                    <div className="flex gap-2"><div className="flex-1 whitespace-pre-wrap text-[#F5F0E8]/85"><strong className="text-[#D4A574]">Prompt:</strong> {prompt.prompt_en}</div><CopyBtn text={prompt.prompt_en} testid={`gap-copy-${index}`} /></div>
                    {prompt.reference_frame_en && <p className="text-[#F5F0E8]/70"><strong className="text-[#D4A574]">Ref frame:</strong> {prompt.reference_frame_en}</p>}
                    {prompt.camera_en && <p className="text-[#F5F0E8]/70"><strong className="text-[#D4A574]">Camera:</strong> {prompt.camera_en}</p>}
                    {prompt.negative_en && <p className="text-[#F5F0E8]/60"><strong className="text-[#D4A574]">Avoid:</strong> {prompt.negative_en}</p>}
                    <div className="mt-3 pt-2 border-t border-white/8 flex flex-wrap gap-2">
                        <input value={importUrl} onChange={e => setImportUrl(e.target.value)} placeholder="https://..." className="flex-1 min-w-[200px] bg-black/30 border border-white/15 rounded px-2 py-1 text-[11px] text-[#F5F0E8]" />
                        <input value={importTool} onChange={e => setImportTool(e.target.value)} className="w-32 bg-black/30 border border-white/15 rounded px-2 py-1 text-[11px] text-[#F5F0E8]" />
                        <button onClick={doImport} disabled={!importUrl} className="rounded-full bg-[#D4A574] text-[#0D0A08] px-3 py-1 text-[10.5px] uppercase tracking-[0.14em] font-bold disabled:opacity-40">{t(lang, "Import", "导入")}</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================
// 4) WORKS — auto-ID, +New work with file
// ============================================================
function WorksPanel({ lang }) {
    const [works, setWorks] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const [open, setOpen] = useState(null);
    const refresh = async () => {
        try {
            const r = await axios.get(`${API}/os/works`, { headers: osHeaders(), withCredentials: true });
            setWorks(r.data.works || []);
        } catch {}
    };
    useEffect(() => { refresh(); }, []);

    return (
        <Section eyebrow={t(lang, "Practice Works", "练习作品")} title={t(lang, "My works & versions", "我的作品与版本")}
            right={<button onClick={() => setShowNew(true)} data-testid="work-add" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-bold" id="works-new"><Plus size={12} className="inline mr-1" /> {t(lang, "New work", "新建作品")}</button>}
            testid="director-versions">

            {showNew && <NewWorkForm lang={lang} onCancel={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}

            <div className="space-y-2">
                {works.length === 0 && !showNew && <p className="text-[12px] text-[#F5F0E8]/40 italic">{t(lang, "No works yet — upload your first result.", "暂无作品 — 上传你的第一次练习结果开始。")}</p>}
                {works.map(w => (
                    <WorkCard key={w._id} work={w} lang={lang} isOpen={open === w._id} onOpen={() => setOpen(open === w._id ? null : w._id)} onRefresh={refresh} />
                ))}
            </div>
        </Section>
    );
}

function NewWorkForm({ lang, onCancel, onSaved }) {
    const [name, setName] = useState("");
    const [ability, setAbility] = useState("brand_direction");
    const [mediaId, setMediaId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const [progress, setProgress] = useState(null);
    const inputRef = useRef(null);

    const upload = async (file) => {
        if (!file) return;
        setProgress({ pct: 0, status: "signing", size: file.size, name: file.name });
        try {
            const { data: presign } = await axios.post(`${API}/os/media/presign-upload`, { content_type: file.type || "video/mp4", size: file.size, filename: file.name }, { headers: osHeaders(), withCredentials: true });
            setProgress(p => ({ ...p, status: "uploading" }));
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", presign.upload_url);
                xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
                xhr.upload.onprogress = e => e.lengthComputable && setProgress(p => ({ ...p, pct: Math.round((e.loaded / e.total) * 100) }));
                xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`R2 ${xhr.status}`));
                xhr.onerror = () => reject(new Error("Network error (if >100MB check R2 CORS)"));
                xhr.send(file);
            });
            await axios.post(`${API}/os/media/confirm-upload`, { media_id: presign.media_id, key: presign.key }, { headers: osHeaders(), withCredentials: true });
            setMediaId(presign.media_id);
            setProgress({ pct: 100, status: "done", size: file.size, name: file.name });
        } catch (e) { setProgress(p => ({ ...p, status: "error", error: e.message })); }
    };

    const save = async () => {
        setSaving(true); setErr(null);
        try {
            await axios.post(`${API}/os/works`, { name, ability_key: ability, media_id: mediaId }, { headers: osHeaders(), withCredentials: true });
            onSaved();
        } catch (e) { setErr(e?.response?.data?.detail || e.message); }
        finally { setSaving(false); }
    };

    const field = "w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-[13px] text-[#F5F0E8] focus:outline-none focus:border-[#D4A574]";

    return (
        <div className="rounded-xl bg-white/5 border border-[#D4A574]/30 p-5 mb-5" data-testid="work-new">
            <input className={field} value={name} onChange={e => setName(e.target.value)} placeholder={t(lang, "Work name *", "作品名称 *")} data-testid="work-name" />
            <select className={`${field} mt-3`} value={ability} onChange={e => setAbility(e.target.value)}>
                <option value="brand_direction">{t(lang, "Brand direction", "品牌导演")}</option>
                <option value="aesthetic_lens">{t(lang, "Aesthetic / lens", "审美与镜头")}</option>
                <option value="edit_sound">{t(lang, "Edit / sound", "剪辑与声音")}</option>
                <option value="lighting_texture">{t(lang, "Lighting / texture", "布光与质感")}</option>
            </select>
            <div className="mt-3">
                <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="work-file" />
                <button onClick={() => inputRef.current?.click()} className="rounded-full border border-white/20 text-[#F5F0E8]/80 px-4 py-2 text-[11px] uppercase tracking-[0.14em]">
                    <Upload size={11} className="inline mr-1" /> {mediaId ? t(lang, "Replace video", "替换视频") : t(lang, "Upload video (optional)", "上传视频（可选）")}
                </button>
                {progress && (
                    <div className="mt-2 text-[11px] text-[#F5F0E8]/60">
                        {progress.name} · {(progress.size / (1024 * 1024)).toFixed(1)} MB · {progress.status} {progress.status === "uploading" && `· ${progress.pct}%`}
                        {progress.status === "error" && <span className="text-[#C0604A]"> · {progress.error}</span>}
                    </div>
                )}
            </div>
            {err && <p className="text-[#C0604A] text-[12px] mt-2">{err}</p>}
            <div className="flex gap-2 mt-3">
                <button onClick={save} disabled={saving || !name} data-testid="work-save" className="rounded-full bg-[#D4A574] text-[#0D0A08] px-5 py-2 text-[11px] uppercase tracking-[0.14em] font-bold disabled:opacity-40">{saving ? "…" : t(lang, "Create work", "创建作品")}</button>
                <button onClick={onCancel} className="text-[11px] text-[#F5F0E8]/50 hover:text-[#F5F0E8] px-3">{t(lang, "Cancel", "取消")}</button>
            </div>
        </div>
    );
}

function WorkCard({ work, lang, isOpen, onOpen, onRefresh }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        if (isOpen && !detail) {
            axios.get(`${API}/os/works/${work._id}`, { headers: osHeaders(), withCredentials: true }).then(r => setDetail(r.data)).catch(() => {});
        }
    }, [isOpen]);

    const moveToRefs = async () => {
        if (!window.confirm(t(lang, "Move this work into My references? (Original work will be archived, not deleted.)", "把此作品移到「我的参考」？（原作品会归档保留，不会删除。）"))) return;
        await axios.post(`${API}/os/works/${work._id}/move-to-references`, {}, { headers: osHeaders(), withCredentials: true });
        onRefresh();
    };
    const archive = async () => {
        if (!window.confirm(t(lang, "Archive this work?", "归档此作品？"))) return;
        await axios.post(`${API}/os/works/${work._id}/archive`, {}, { headers: osHeaders(), withCredentials: true });
        onRefresh();
    };

    return (
        <div className="rounded-xl bg-white/3 border border-white/8 p-4" data-testid={`work-${work._id}`}>
            <button onClick={onOpen} className="w-full text-left">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="font-serif text-base text-[#F5F0E8]">{work.name}</p>
                        <p className="text-[11px] text-[#F5F0E8]/50 mt-0.5">{new Date(work.created_at).toLocaleDateString()} · {work.ability_key || "—"} · {work.status}</p>
                    </div>
                    <ChevronRight size={16} className={`shrink-0 text-[#F5F0E8]/40 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </div>
            </button>
            {isOpen && detail && (
                <div className="mt-4 pt-4 border-t border-white/8 space-y-3">
                    {detail.versions.map((v, i) => (
                        <div key={v._id} className="flex items-center gap-3 text-[11.5px] border-l-2 border-[#D4A574] pl-3" data-testid={`work-${work._id}-v${i}`}>
                            <strong className="text-[#F5F0E8]/85">{v.label}</strong>
                            <span className="text-[#F5F0E8]/50">{v.change_reason}</span>
                            <span className="text-[#F5F0E8]/35 text-[10.5px] ml-auto">{new Date(v.created_at).toLocaleDateString()}</span>
                        </div>
                    ))}
                    <div className="flex flex-wrap gap-2 pt-3">
                        <button onClick={moveToRefs} data-testid={`move-ref-${work._id}`} className="text-[10.5px] uppercase tracking-[0.14em] text-[#D4A574] border border-[#D4A574]/40 rounded-full px-3 py-1">{t(lang, "Move to references", "移到参考库")}</button>
                        <button onClick={archive} className="text-[10.5px] uppercase tracking-[0.14em] text-[#F5F0E8]/50 border border-white/15 rounded-full px-3 py-1"><Archive size={10} className="inline mr-1" /> {t(lang, "Archive", "归档")}</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================
// Mounted entry point
// ============================================================
export default function OSDirectorTraining({ lang = "cn", only = null, fallbackLegacy = false }) {
    const show = (k) => !only || only === k;
    return (
        <div className="space-y-8" data-testid="os-director-training">
            {show("abilities") && <AbilitiesPanel lang={lang} />}
            {show("abilities") && fallbackLegacy && <OSPracticeToday lang={lang} />}
            {show("references") && <ReferencesPanel lang={lang} />}
            {show("footage") && <CreativeLabPanel lang={lang} />}
            {show("versions") && <WorksPanel lang={lang} />}
        </div>
    );
}
